import { SdkLogger } from './sdkLogger';
import { CapabilityThrottle } from './capabilityThrottle';

/**
 * Log prefix for all capability monitoring logs
 * Used across SDK to identify capability-related logs
 */
export const CAPABILITY_MONITOR_LOG_PREFIX = '[CapabilityMonitor]';

/**
 * WakeLock API types (experimental API)
 * Needed to fix the type error for the wakeLock.request method in older browsers
 */
type NavigatorWithWakeLock = Navigator & {
  wakeLock?: {
    request(type?: 'screen'): Promise<unknown>;
  };
};

/**
 * Capability types that require manifest permissions
 */
export enum PrivilegedCapability {
  CLIPBOARD_WRITE = 'clipboard-write',
  MICROPHONE = 'microphone',
  CAMERA = 'camera',
  SCREEN_WAKE_LOCK = 'screen-wake-lock',
  COMPUTE_PRESSURE = 'compute-pressure',
}

/**
 * Sandbox features that don't require permissions but should be monitored
 */
export enum SandboxFeature {
  PRINT = 'print',
  MODAL_ALERT = 'modal-alert',
  MODAL_CONFIRM = 'modal-confirm',
  MODAL_PROMPT = 'modal-prompt',
  POPUP = 'popup',
  FORM_SUBMIT = 'form-submit',
  DOWNLOAD = 'download',
}

/**
 * Detection method used to identify capability usage
 */
export enum DetectionMethod {
  API_HOOK = 'api-hook',
  PERMISSION_QUERY = 'permission-query',
  ERROR_DETECTION = 'error-detection',
}

// Shared throttle for all capability logging (logs each capability once per SDK instance)
const sharedThrottle = new CapabilityThrottle();

/**
 * Clear the shared throttle (primarily for testing)
 * @internal
 */
export const clearSharedThrottle = (): void => {
  sharedThrottle.clear();
};

/**
 * Log capability triggered/attempted (both privileged capabilities and sandbox features)
 * Uses shared throttle - logs each capability only once per SDK instance
 */
export const notifyCapabilityTriggered = (logger: SdkLogger, capability: string): void => {
  if (sharedThrottle.isThrottled(capability)) {
    return;
  }

  logger.info(`${CAPABILITY_MONITOR_LOG_PREFIX} Capability triggered`, {
    capability,
    detectionMethod: DetectionMethod.API_HOOK,
  });
  sharedThrottle.updateThrottle(capability);
};

/**
 * Log capability failed or blocked
 * Uses shared throttle - logs each capability failure/block only once per SDK instance
 */
export const notifyCapabilityFailed = (
  logger: SdkLogger,
  capability: string,
  error?: unknown,
): void => {
  const failedKey = error ? `${capability}-failed` : `${capability}-blocked`;
  if (sharedThrottle.isThrottled(failedKey)) {
    return;
  }

  const message = error
    ? `${CAPABILITY_MONITOR_LOG_PREFIX} Capability failed`
    : `${CAPABILITY_MONITOR_LOG_PREFIX} Capability blocked`;

  const logData: Record<string, unknown> = {
    capability,
    detectionMethod: error ? DetectionMethod.ERROR_DETECTION : DetectionMethod.API_HOOK,
  };

  if (error) {
    logData.error = error;
  }

  logger.warn(message, logData);
  sharedThrottle.updateThrottle(failedKey);
};

/**
 * Monitors browser capability usage and sends messages to Vim OS parent
 */
export class CapabilityMonitor {
  private readonly originalMethods: Map<string, unknown> = new Map();

  constructor(private readonly windowObj: Window, private readonly logger: SdkLogger) {}

  /**
   * Initialize all capability monitoring
   */
  initialize(): void {
    this.monitorPrivilegedCapabilities();
    this.monitorSandboxFeatures();
  }

  /**
   * Monitor privileged capabilities (microphone, camera, etc.)
   */
  private monitorPrivilegedCapabilities(): void {
    this.monitorMediaDevices();
    this.monitorClipboard();
    this.monitorWakeLock();
    this.monitorComputePressure();
  }

  /**
   * Monitor sandbox features (print, modals, popups, forms, downloads)
   */
  private monitorSandboxFeatures(): void {
    this.monitorPrint();
    this.monitorPopups();
    this.monitorForms();
    this.monitorDownloads();
    // Note: window modals are already monitored by WindowMethodBlocker
    // We just need to send the capability monitoring message
  }

  /**
   * Monitor getUserMedia (microphone, camera)
   */
  private monitorMediaDevices(): void {
    try {
      if (!this.windowObj.navigator?.mediaDevices?.getUserMedia) {
        return;
      }

      const original = this.windowObj.navigator.mediaDevices.getUserMedia.bind(
        this.windowObj.navigator.mediaDevices,
      );
      this.originalMethods.set('getUserMedia', original);

      this.windowObj.navigator.mediaDevices.getUserMedia = async (
        constraints?: MediaStreamConstraints,
      ): Promise<MediaStream> => {
        try {
          const result = await original(constraints);

          // Log successful capability usage after the API succeeds
          if (constraints?.audio) {
            notifyCapabilityTriggered(this.logger, PrivilegedCapability.MICROPHONE);
          }
          if (constraints?.video) {
            notifyCapabilityTriggered(this.logger, PrivilegedCapability.CAMERA);
          }

          return result;
        } catch (error) {
          // Capability failed
          if (constraints?.audio) {
            notifyCapabilityFailed(this.logger, PrivilegedCapability.MICROPHONE, error);
          }
          if (constraints?.video) {
            notifyCapabilityFailed(this.logger, PrivilegedCapability.CAMERA, error);
          }
          throw error;
        }
      };
    } catch (error) {
      this.logger.warn(`${CAPABILITY_MONITOR_LOG_PREFIX} Failed to monitor media devices`, {
        error,
      });
    }
  }

  /**
   * Monitor clipboard write operations
   */
  private monitorClipboard(): void {
    try {
      if (!this.windowObj.navigator?.clipboard?.writeText) {
        return;
      }

      const original = this.windowObj.navigator.clipboard.writeText.bind(
        this.windowObj.navigator.clipboard,
      );
      this.originalMethods.set('clipboardWriteText', original);

      this.windowObj.navigator.clipboard.writeText = async (text: string): Promise<void> => {
        try {
          const result = await original(text);
          // Log successful capability usage after the API succeeds
          notifyCapabilityTriggered(this.logger, PrivilegedCapability.CLIPBOARD_WRITE);
          return result;
        } catch (error) {
          notifyCapabilityFailed(this.logger, PrivilegedCapability.CLIPBOARD_WRITE, error);
          throw error;
        }
      };

      // Also monitor clipboard.write() for richer clipboard operations
      if (this.windowObj.navigator?.clipboard?.write) {
        const originalWrite = this.windowObj.navigator.clipboard.write.bind(
          this.windowObj.navigator.clipboard,
        );
        this.originalMethods.set('clipboardWrite', originalWrite);

        this.windowObj.navigator.clipboard.write = async (data: ClipboardItems): Promise<void> => {
          try {
            const result = await originalWrite(data);
            // Log successful capability usage after the API succeeds
            notifyCapabilityTriggered(this.logger, PrivilegedCapability.CLIPBOARD_WRITE);
            return result;
          } catch (error) {
            notifyCapabilityFailed(this.logger, PrivilegedCapability.CLIPBOARD_WRITE, error);
            throw error;
          }
        };
      }
    } catch (error) {
      this.logger.warn(`${CAPABILITY_MONITOR_LOG_PREFIX} Failed to monitor clipboard`, { error });
    }
  }

  /**
   * Monitor screen wake lock
   */
  private monitorWakeLock(): void {
    try {
      const nav = this.windowObj.navigator as NavigatorWithWakeLock;
      if (!nav?.wakeLock?.request) {
        return;
      }

      const original = nav.wakeLock.request.bind(nav.wakeLock);
      this.originalMethods.set('wakeLockRequest', original);

      (nav.wakeLock.request as (type?: 'screen') => Promise<unknown>) = async (
        type?: 'screen',
      ): Promise<unknown> => {
        try {
          const result = await original(type);
          // Log successful capability usage after the API succeeds
          notifyCapabilityTriggered(this.logger, PrivilegedCapability.SCREEN_WAKE_LOCK);
          return result;
        } catch (error) {
          notifyCapabilityFailed(this.logger, PrivilegedCapability.SCREEN_WAKE_LOCK, error);
          throw error;
        }
      };
    } catch (error) {
      this.logger.warn(`${CAPABILITY_MONITOR_LOG_PREFIX} Failed to monitor wake lock`, { error });
    }
  }

  /**
   * Monitor compute pressure API (PressureObserver)
   */
  private monitorComputePressure(): void {
    try {
      // @ts-expect-error - PressureObserver is experimental API
      if (!this.windowObj.PressureObserver) {
        return;
      }

      // @ts-expect-error - PressureObserver is experimental API
      const OriginalObserver = this.windowObj.PressureObserver;
      this.originalMethods.set('PressureObserver', OriginalObserver);

      const logger = this.logger;
      // @ts-expect-error - PressureObserver is experimental API
      this.windowObj.PressureObserver = class extends OriginalObserver {
        constructor(...args: any[]) {
          super(...args);
          // Log successful capability usage after the observer is created
          notifyCapabilityTriggered(logger, PrivilegedCapability.COMPUTE_PRESSURE);
        }
      };
    } catch (error) {
      this.logger.warn(`${CAPABILITY_MONITOR_LOG_PREFIX} Failed to monitor compute pressure`, {
        error,
      });
    }
  }

  /**
   * Monitor window.print()
   */
  private monitorPrint(): void {
    try {
      const original = this.windowObj.print.bind(this.windowObj);
      this.originalMethods.set('print', original);

      this.windowObj.print = (): void => {
        const result = original();
        // Log successful capability usage after the API succeeds
        notifyCapabilityTriggered(this.logger, SandboxFeature.PRINT);
        return result;
      };
    } catch (error) {
      this.logger.warn(`${CAPABILITY_MONITOR_LOG_PREFIX} Failed to monitor print`, { error });
    }
  }

  /**
   * Monitor window.open() for popup detection
   */
  private monitorPopups(): void {
    try {
      if (!this.windowObj.open) {
        return;
      }

      const original = this.windowObj.open.bind(this.windowObj);
      this.originalMethods.set('open', original);

      this.windowObj.open = (
        url?: string | URL,
        target?: string,
        features?: string,
      ): Window | null => {
        const result = original(url, target, features);
        // Log successful capability usage after the API succeeds
        notifyCapabilityTriggered(this.logger, SandboxFeature.POPUP);
        return result;
      };
    } catch (error) {
      this.logger.warn(`${CAPABILITY_MONITOR_LOG_PREFIX} Failed to monitor popups`, { error });
    }
  }

  /**
   * Monitor form submissions
   */
  private monitorForms(): void {
    try {
      // Access HTMLFormElement from the global scope
      if (!HTMLFormElement?.prototype?.submit) {
        return;
      }

      const original = HTMLFormElement.prototype.submit;
      this.originalMethods.set('formSubmit', original);

      const logger = this.logger;

      HTMLFormElement.prototype.submit = function (): void {
        // Use a non-arrow function to preserve 'this' context for the form element
        const result = original.call(this);
        // Log successful capability usage after the API succeeds
        notifyCapabilityTriggered(logger, SandboxFeature.FORM_SUBMIT);
        return result;
      };
    } catch (error) {
      this.logger.warn(`${CAPABILITY_MONITOR_LOG_PREFIX} Failed to monitor forms`, { error });
    }
  }

  /**
   * Monitor download operations via <a download> elements
   */
  private monitorDownloads(): void {
    try {
      // Monitor clicks on <a download> elements
      const logger = this.logger;
      const clickHandler = (event: Event): void => {
        const target = event.target;

        // Guard against non-HTMLElement targets (e.g., Text nodes)
        if (!(target instanceof HTMLElement)) {
          return;
        }

        // Check if it's an anchor element with download attribute
        if (target.tagName === 'A' && target.hasAttribute('download')) {
          notifyCapabilityTriggered(logger, SandboxFeature.DOWNLOAD);
        }
        // Also check if clicked element is inside an anchor with download
        const anchor = target.closest('a[download]');
        if (anchor) {
          notifyCapabilityTriggered(logger, SandboxFeature.DOWNLOAD);
        }
      };

      this.windowObj.document.addEventListener('click', clickHandler, true);
      this.originalMethods.set('downloadClickHandler', clickHandler);
    } catch (error) {
      this.logger.warn(`${CAPABILITY_MONITOR_LOG_PREFIX} Failed to monitor downloads`, { error });
    }
  }

  /**
   * Restore all original methods and clean up event listeners.
   *
   * This method is provided for proper cleanup/teardown scenarios, such as:
   * - SDK disposal/unmounting
   * - Testing environments that need to reset state
   * - Hot module replacement during development
   *
   * Note: Currently not called in the SDK lifecycle as apps run for the entire
   * session, but available for future use cases or explicit cleanup needs.
   */
  restore(): void {
    // Restore media devices
    const getUserMedia = this.originalMethods.get('getUserMedia');
    if (getUserMedia && this.windowObj.navigator?.mediaDevices) {
      this.windowObj.navigator.mediaDevices.getUserMedia =
        getUserMedia as typeof this.windowObj.navigator.mediaDevices.getUserMedia;
    }

    // Restore clipboard
    const clipboardWriteText = this.originalMethods.get('clipboardWriteText');
    if (clipboardWriteText && this.windowObj.navigator?.clipboard) {
      this.windowObj.navigator.clipboard.writeText =
        clipboardWriteText as typeof this.windowObj.navigator.clipboard.writeText;
    }

    const clipboardWrite = this.originalMethods.get('clipboardWrite');
    if (clipboardWrite && this.windowObj.navigator?.clipboard) {
      this.windowObj.navigator.clipboard.write =
        clipboardWrite as typeof this.windowObj.navigator.clipboard.write;
    }

    // Restore wake lock
    const wakeLockRequest = this.originalMethods.get('wakeLockRequest');
    const nav = this.windowObj.navigator as NavigatorWithWakeLock;
    if (wakeLockRequest && nav?.wakeLock) {
      nav.wakeLock.request = wakeLockRequest as typeof nav.wakeLock.request;
    }

    // Restore compute pressure
    const PressureObserver = this.originalMethods.get('PressureObserver');
    if (PressureObserver) {
      // @ts-expect-error - PressureObserver is experimental API
      this.windowObj.PressureObserver = PressureObserver;
    }

    // Restore print
    const print = this.originalMethods.get('print');
    if (print) {
      this.windowObj.print = print as Window['print'];
    }

    // Restore popups
    const open = this.originalMethods.get('open');
    if (open) {
      this.windowObj.open = open as Window['open'];
    }

    // Restore forms
    const formSubmit = this.originalMethods.get('formSubmit');
    if (formSubmit && HTMLFormElement?.prototype) {
      HTMLFormElement.prototype.submit = formSubmit as typeof HTMLFormElement.prototype.submit;
    }

    // Restore downloads
    const downloadClickHandler = this.originalMethods.get('downloadClickHandler');
    if (downloadClickHandler) {
      this.windowObj.document.removeEventListener(
        'click',
        downloadClickHandler as EventListener,
        true,
      );
    }

    this.originalMethods.clear();
  }
}
